import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizAPI, flashcardAPI, interviewAPI, learningAssetAPI, BASE_URL } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TakeQuiz from './TakeQuiz';
import AssetQuizInterface from './AssetQuizInterface';
import { useAuth } from '../context/AuthContext';

// Document Watermark Component
const VideoWatermark = ({ userName, userEmail }) => {
  const [position, setPosition] = useState({ top: '10%', left: '10%' });
  const [opacity, setOpacity] = useState(0.3);

  useEffect(() => {
    const moveWatermark = () => {
      // Random positions (avoiding edges)
      const top = Math.random() * 70 + 10; // 10-80%
      const left = Math.random() * 70 + 10; // 10-80%
      setPosition({ top: `${top}%`, left: `${left}%` });
      
      // Vary opacity slightly
      setOpacity(Math.random() * 0.2 + 0.25); // 0.25-0.45
    };

    // Move watermark every 8-15 seconds
    const getRandomInterval = () => Math.random() * 7000 + 8000;
    
    let timeoutId;
    const scheduleNextMove = () => {
      timeoutId = setTimeout(() => {
        moveWatermark();
        scheduleNextMove();
      }, getRandomInterval());
    };
    
    scheduleNextMove();

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        transform: 'translate(-50%, -50%)',
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: '500',
        padding: '6px 12px',
        background: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '6px',
        pointerEvents: 'none',
        userSelect: 'none',
        zIndex: 999,
        opacity: opacity,
        transition: 'all 1.5s ease-in-out',
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
        whiteSpace: 'nowrap',
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      <div style={{ fontSize: '12px', marginBottom: '2px' }}>{userName}</div>
      <div style={{ fontSize: '10px', opacity: 0.9 }}>{userEmail}</div>
    </div>
  );
};

// Helper functions to parse video URLs
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const getVimeoVideoId = (url) => {
  if (!url) return null;
  // Match vimeo.com/VIDEO_ID or vimeo.com/VIDEO_ID/HASH
  const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (match) {
    // Return video ID and optional privacy hash
    return match[2] ? `${match[1]}?h=${match[2]}` : match[1];
  }
  return null;
};

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&origin=${window.location.origin}`;
  }
  
  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) {
    // Add parameters for better compatibility and privacy
    // autopause=0: Don't pause when another video plays
    // player_id=0: Unique player identifier
    return `https://player.vimeo.com/video/${vimeoId}${vimeoId.includes('?') ? '&' : '?'}dnt=1&byline=0&portrait=0&title=0&autopause=0`;
  }
  
  return null;
};

// Study Questions Component - Accordion display
const StudyQuestionsSection = ({ questions, isDarkMode }) => {
  const [expandedQuestion, setExpandedQuestion] = React.useState(null);

  if (!questions || questions.length === 0) return null;

  const toggleQuestion = (index) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };

  return (
    <div style={{
      marginTop: '40px',
      padding: '24px',
      background: isDarkMode ? '#374151' : '#f7fafc',
      borderRadius: '12px',
      border: `2px solid ${isDarkMode ? '#4b5563' : '#e2e8f0'}`
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <span style={{ fontSize: '28px' }}>💡</span>
        <div>
          <h3 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: '600',
            color: isDarkMode ? '#f3f4f6' : '#1a202c'
          }}>
            Study Questions
          </h3>
          <p style={{ 
            margin: '4px 0 0 0', 
            fontSize: '14px', 
            color: isDarkMode ? '#d1d5db' : '#718096'
          }}>
            Click on any question to reveal the answer
          </p>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {questions.map((q, index) => {
          const isExpanded = expandedQuestion === index;
          
          return (
            <div
              key={index}
              style={{
                background: isDarkMode ? '#2d3748' : '#ffffff',
                borderRadius: '8px',
                border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                overflow: 'hidden',
                transition: 'all 0.2s'
              }}
            >
              <div
                onClick={() => toggleQuestion(index)}
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  background: isExpanded 
                    ? (isDarkMode ? '#374151' : '#f7fafc')
                    : (isDarkMode ? '#2d3748' : '#ffffff'),
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.background = isDarkMode ? '#2d3748' : '#ffffff';
                  }
                }}
              >
                <div style={{
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: isDarkMode ? '#667eea' : '#edf2f7',
                  color: isDarkMode ? '#ffffff' : '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>
                <div style={{
                  flex: 1,
                  fontSize: '15px',
                  fontWeight: '600',
                  color: isDarkMode ? '#e2e8f0' : '#1a202c',
                  lineHeight: '1.5'
                }}>
                  {q.question}
                </div>
                <div style={{
                  fontSize: '20px',
                  color: isDarkMode ? '#9ca3af' : '#cbd5e0',
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  flexShrink: 0
                }}>
                  ▼
                </div>
              </div>
              
              {isExpanded && (
                <div style={{
                  padding: '20px',
                  paddingTop: '16px',
                  background: isDarkMode ? '#1a202c' : '#f7fafc',
                  borderTop: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#48bb78',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>✓</span> Answer
                  </div>
                  <div style={{
                    fontSize: '15px',
                    color: isDarkMode ? '#d1d5db' : '#2d3748',
                    lineHeight: '1.7',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {q.answer}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function AssetRenderer({ asset, onComplete, isCompleted, isDarkMode = false, onInteraction }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referencedContent, setReferencedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const videoRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Flashcard-specific state
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studiedCards, setStudiedCards] = useState(new Set());
  
  // Asset quiz tracking
  const [quizData, setQuizData] = useState({ hasQuiz: false, hasAttempts: false });

  useEffect(() => {
    // Reset quiz active state when asset changes
    setIsQuizActive(false);
    
    // Reset flashcard state
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setStudiedCards(new Set());
    
    if (asset.contentReference?.refModel && asset.contentReference?.refId) {
      fetchReferencedContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset]);

  // Track YouTube/Vimeo video progress
  useEffect(() => {
    if (asset.assetType !== 'video' || !asset.externalUrl) return;

    const youtubeId = getYouTubeVideoId(asset.externalUrl);
    const vimeoId = getVimeoVideoId(asset.externalUrl);

    // Load and initialize YouTube player
    if (youtubeId && videoContainerRef.current) {
      // Load YouTube API if not already loaded
      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          initYouTubePlayer(youtubeId);
        };
      } else if (window.YT.Player) {
        initYouTubePlayer(youtubeId);
      }
    }

    // Load and initialize Vimeo player
    if (vimeoId && videoContainerRef.current) {
      // Load Vimeo API if not already loaded
      if (!window.Vimeo) {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = () => {
          initVimeoPlayer(vimeoId);
        };
        document.body.appendChild(script);
      } else {
        initVimeoPlayer(vimeoId);
      }
    }

    return () => {
      // Cleanup players
      if (youtubePlayerRef.current && youtubePlayerRef.current.destroy) {
        youtubePlayerRef.current.destroy();
        youtubePlayerRef.current = null;
      }
      if (vimeoPlayerRef.current && vimeoPlayerRef.current.destroy) {
        vimeoPlayerRef.current.destroy();
        vimeoPlayerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset]);

  const initYouTubePlayer = (videoId) => {
    if (!videoContainerRef.current) return;

    // Clear container before creating new player
    videoContainerRef.current.innerHTML = '';
    
    // Create a div for the YouTube player
    const playerDiv = document.createElement('div');
    playerDiv.style.position = 'absolute';
    playerDiv.style.top = '0';
    playerDiv.style.left = '0';
    playerDiv.style.width = '100%';
    playerDiv.style.height = '100%';
    videoContainerRef.current.appendChild(playerDiv);

    youtubePlayerRef.current = new window.YT.Player(playerDiv, {
      videoId: videoId,
      playerVars: {
        autoplay: 0,
        modestbranding: 1,
        rel: 0
      },
      events: {
        onReady: (event) => {
          const duration = event.target.getDuration();
          learningAssetAPI.updateVideoProgress(asset._id, 0, Math.floor(duration))
            .catch(err => console.error('Error initializing video progress:', err));

          // Start tracking progress every 5 seconds
          progressIntervalRef.current = setInterval(() => {
            if (youtubePlayerRef.current && youtubePlayerRef.current.getCurrentTime) {
              const currentTime = youtubePlayerRef.current.getCurrentTime();
              const duration = youtubePlayerRef.current.getDuration();
              learningAssetAPI.updateVideoProgress(asset._id, Math.floor(currentTime), Math.floor(duration))
                .catch(err => console.error('Error updating video progress:', err));
            }
          }, 5000);
        },
        onStateChange: (event) => {
          // Save progress on pause or end
          if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
            const currentTime = event.target.getCurrentTime();
            const duration = event.target.getDuration();
            learningAssetAPI.updateVideoProgress(asset._id, Math.floor(currentTime), Math.floor(duration))
              .catch(err => console.error('Error saving video progress:', err));
            
            if (event.data === window.YT.PlayerState.ENDED) {
              handleMarkComplete();
            }
          }
        }
      }
    });
  };

  const initVimeoPlayer = (videoId) => {
    if (!videoContainerRef.current || !window.Vimeo) return;

    // Create iframe element with Vimeo URL
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${videoId}`;
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    
    // Clear container and append new iframe
    videoContainerRef.current.innerHTML = '';
    videoContainerRef.current.appendChild(iframe);
    
    // Initialize Vimeo player with the iframe
    vimeoPlayerRef.current = new window.Vimeo.Player(iframe);

    vimeoPlayerRef.current.getDuration().then((duration) => {
      learningAssetAPI.updateVideoProgress(asset._id, 0, Math.floor(duration))
        .catch(err => console.error('Error initializing video progress:', err));
    }).catch(err => console.error('Error getting duration:', err));

    // Track progress every 5 seconds
    progressIntervalRef.current = setInterval(() => {
      if (vimeoPlayerRef.current) {
        Promise.all([
          vimeoPlayerRef.current.getCurrentTime(),
          vimeoPlayerRef.current.getDuration()
        ]).then(([currentTime, duration]) => {
          learningAssetAPI.updateVideoProgress(asset._id, Math.floor(currentTime), Math.floor(duration))
            .catch(err => console.error('Error updating video progress:', err));
        }).catch(err => console.error('Error getting video time:', err));
      }
    }, 5000);

    // Save progress on pause
    vimeoPlayerRef.current.on('pause', () => {
      Promise.all([
        vimeoPlayerRef.current.getCurrentTime(),
        vimeoPlayerRef.current.getDuration()
      ]).then(([currentTime, duration]) => {
        learningAssetAPI.updateVideoProgress(asset._id, Math.floor(currentTime), Math.floor(duration))
          .catch(err => console.error('Error saving video progress:', err));
      }).catch(err => console.error('Error on pause:', err));
    });

    // Save progress and mark complete on end
    vimeoPlayerRef.current.on('ended', () => {
      Promise.all([
        vimeoPlayerRef.current.getCurrentTime(),
        vimeoPlayerRef.current.getDuration()
      ]).then(([currentTime, duration]) => {
        learningAssetAPI.updateVideoProgress(asset._id, Math.floor(currentTime), Math.floor(duration))
          .catch(err => console.error('Error saving video progress:', err));
        handleMarkComplete();
      }).catch(err => console.error('Error on ended:', err));
    });
  };

  // Track uploaded video progress
  useEffect(() => {
    if (asset.assetType !== 'video' || !videoRef.current || asset.externalUrl) return;

    const video = videoRef.current;

    const updateProgress = () => {
      if (video.duration && !isNaN(video.duration)) {
        const watchedSeconds = Math.floor(video.currentTime);
        const totalDuration = Math.floor(video.duration);
        
        // Update progress every 10 seconds of watch time
        if (watchedSeconds > 0 && watchedSeconds % 10 === 0) {
          learningAssetAPI.updateVideoProgress(asset._id, watchedSeconds, totalDuration)
            .catch(err => console.error('Error updating video progress:', err));
        }
      }
    };

    const handleTimeUpdate = () => {
      updateProgress();
    };

    const handleLoadedMetadata = () => {
      // Initialize progress when video metadata is loaded
      if (video.duration && !isNaN(video.duration)) {
        const totalDuration = Math.floor(video.duration);
        learningAssetAPI.updateVideoProgress(asset._id, 0, totalDuration)
          .catch(err => console.error('Error initializing video progress:', err));
      }
    };

    const handlePause = () => {
      // Save progress when video is paused
      if (video.currentTime && video.duration) {
        const watchedSeconds = Math.floor(video.currentTime);
        const totalDuration = Math.floor(video.duration);
        learningAssetAPI.updateVideoProgress(asset._id, watchedSeconds, totalDuration)
          .catch(err => console.error('Error saving video progress:', err));
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('pause', handlePause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset]);


  const fetchReferencedContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const { refModel, refId } = asset.contentReference;
      
      if (refModel === 'Quiz') {
        const response = await quizAPI.getQuizById(refId);
        setReferencedContent(response.data);
      } else if (refModel === 'FlashCard') {
        const response = await flashcardAPI.getFlashcard(refId);
        // Backend returns { flashCard, terms } - combine them for display
        setReferencedContent({
          ...response.data.flashCard,
          terms: response.data.terms
        });
      } else if (refModel === 'Interview') {
        const response = await interviewAPI.getInterview(refId);
        setReferencedContent(response.data.interview);
      }
    } catch (err) {
      console.error('Error fetching referenced content:', err);
      setError(err.response?.data?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = () => {
    console.log('handleMarkComplete called', { 
      assetId: asset._id, 
      hasOnComplete: !!onComplete, 
      isCompleted 
    });
    if (onComplete) {
      console.log('Calling onComplete with asset ID:', asset._id);
      onComplete(asset._id);
    } else {
      console.log('Skip completion: no onComplete handler');
    }
  };

  // Render content based on asset type
  if (asset.assetType === 'note') {
    return (
      <>
      <div className="asset-renderer" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ 
          background: isDarkMode ? '#1a202c' : '#ffffff', 
          padding: '24px', 
          borderRadius: '8px',
          minHeight: '200px'
        }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, children, ...props}) => <h1 style={{ color: isDarkMode ? '#f7fafc' : '#1a202c', marginTop: '24px', marginBottom: '16px' }} {...props}>{children}</h1>,
              h2: ({node, children, ...props}) => <h2 style={{ color: isDarkMode ? '#f7fafc' : '#1a202c', marginTop: '20px', marginBottom: '12px' }} {...props}>{children}</h2>,
              h3: ({node, children, ...props}) => <h3 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginTop: '16px', marginBottom: '10px' }} {...props}>{children}</h3>,
              h4: ({node, children, ...props}) => <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h4>,
              h5: ({node, children, ...props}) => <h5 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h5>,
              h6: ({node, children, ...props}) => <h6 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h6>,
              p: ({node, children, ...props}) => <p style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</p>,
              li: ({node, children, ...props}) => <li style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</li>,
              a: ({node, children, ...props}) => <a style={{ color: isDarkMode ? '#90cdf4' : '#667eea' }} {...props}>{children}</a>,
              strong: ({node, children, ...props}) => <strong style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</strong>,
              em: ({node, children, ...props}) => <em style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</em>,
              blockquote: ({node, ...props}) => (
                <blockquote style={{ 
                  borderLeft: `4px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  paddingLeft: '16px',
                  color: isDarkMode ? '#a0aec0' : '#718096',
                  fontStyle: 'italic',
                  background: isDarkMode ? '#1a202c' : '#f7fafc',
                  padding: '12px 16px',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }} {...props} />
              ),
              table: ({node, ...props}) => (
                <table style={{ 
                  borderCollapse: 'collapse',
                  width: '100%',
                  marginBottom: '16px',
                  background: isDarkMode ? '#2d3748' : '#ffffff',
                  color: isDarkMode ? '#e2e8f0' : '#1a202c'
                }} {...props} />
              ),
              th: ({node, ...props}) => (
                <th style={{ 
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  padding: '12px 8px',
                  background: isDarkMode ? '#374151' : '#f7fafc',
                  color: isDarkMode ? '#f3f4f6' : '#1a202c',
                  fontWeight: '600',
                  textAlign: 'left'
                }} {...props} />
              ),
              td: ({node, ...props}) => (
                <td style={{ 
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  padding: '10px 8px',
                  background: isDarkMode ? '#2d3748' : '#ffffff',
                  color: isDarkMode ? '#e2e8f0' : '#1a202c'
                }} {...props} />
              ),
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{ fontSize: '16px', lineHeight: '1.6' }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props} style={{ background: isDarkMode ? '#1a202c' : '#f7fafc', padding: '3px 8px', borderRadius: '4px', fontSize: '16px', color: isDarkMode ? '#e2e8f0' : '#1a202c' }}>
                    {children}
                  </code>
                );
              }
            }}
          >
            {asset.content}
          </ReactMarkdown>
        </div>
        
        <StudyQuestionsSection 
          questions={asset.studyQuestions}
          isDarkMode={isDarkMode}
        />
        
        <AssetQuizInterface 
          asset={asset} 
          isDarkMode={isDarkMode} 
          onQuizDataChange={setQuizData}
        />
        
        {/* Show message if quiz exists but not attempted */}
        {quizData.hasQuiz && !quizData.hasAttempts && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: isDarkMode ? '#2d3748' : '#fef5e7',
            borderRadius: '8px',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#f9e79f'}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>ℹ️</span>
            <span style={{
              fontSize: '14px',
              color: isDarkMode ? '#fbbf24' : '#f39c12',
              fontWeight: '500'
            }}>
              You must complete the quiz at least once before marking this asset as complete.
            </span>
          </div>
        )}
        
        <label
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 18px',
            background: isDarkMode ? '#374151' : '#f7fafc',
            borderRadius: '8px',
            cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
            border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
            transition: 'all 0.2s',
            userSelect: 'none',
            opacity: (quizData.hasQuiz && !quizData.hasAttempts) ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
              e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
            }
          }}
          onMouseLeave={(e) => {
            if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
              e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
            }
          }}
        >
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleMarkComplete}
            disabled={quizData.hasQuiz && !quizData.hasAttempts}
            style={{
              width: '20px',
              height: '20px',
              cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
              accentColor: '#48bb78'
            }}
          />
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
          }}>
            {isCompleted ? '✓ Completed' : 'Mark as Complete'}
          </span>
        </label>
      </div>
    </>
    );
  }
  // Transcript (AI-processed)
  if (asset.assetType === 'transcript' && asset.content) {
    return (
      <>
        <div style={{
          background: isDarkMode ? '#2d3748' : '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
        }}>
          <div style={{ 
            marginBottom: '20px', 
            padding: '14px', 
            background: isDarkMode ? '#1a365d' : '#e6f7ff', 
            borderLeft: `4px solid ${isDarkMode ? '#4299e1' : '#1890ff'}`,
            borderRadius: '6px' 
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: isDarkMode ? '#90cdf4' : '#0050b3', fontWeight: '500' }}>
              📝 <strong>Learning Summary:</strong> This content has been automatically organized and summarized from a transcript to help you learn more effectively.
            </p>
          </div>
          
          <div className="markdown-content" style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', fontSize: '18px', lineHeight: '1.8' }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, children, ...props}) => <h1 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h1>,
                h2: ({node, children, ...props}) => <h2 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginTop: '24px', marginBottom: '12px' }} {...props}>{children}</h2>,
                h3: ({node, children, ...props}) => <h3 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginTop: '20px', marginBottom: '10px' }} {...props}>{children}</h3>,
                h4: ({node, children, ...props}) => <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h4>,
                h5: ({node, children, ...props}) => <h5 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h5>,
                h6: ({node, children, ...props}) => <h6 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h6>,
                p: ({node, children, ...props}) => <p style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</p>,
                li: ({node, children, ...props}) => <li style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginBottom: '6px' }} {...props}>{children}</li>,
                ul: ({node, children, ...props}) => <ul style={{ marginLeft: '20px', marginBottom: '16px' }} {...props}>{children}</ul>,
                ol: ({node, children, ...props}) => <ol style={{ marginLeft: '20px', marginBottom: '16px' }} {...props}>{children}</ol>,
                a: ({node, children, ...props}) => <a style={{ color: isDarkMode ? '#90cdf4' : '#667eea' }} {...props}>{children}</a>,
                strong: ({node, children, ...props}) => <strong style={{ color: isDarkMode ? '#f7fafc' : '#1a202c', fontWeight: '600' }} {...props}>{children}</strong>,
                em: ({node, children, ...props}) => <em style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</em>,
                blockquote: ({node, ...props}) => (
                  <blockquote style={{ 
                    borderLeft: `4px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                    color: isDarkMode ? '#a0aec0' : '#718096',
                    fontStyle: 'italic',
                    background: isDarkMode ? '#1a202c' : '#f7fafc',
                    padding: '12px 16px',
                    borderRadius: '4px',
                    marginBottom: '16px'
                  }} {...props} />
                ),
                table: ({node, ...props}) => (
                  <table style={{ 
                    borderCollapse: 'collapse',
                    width: '100%',
                    marginBottom: '16px',
                    background: isDarkMode ? '#2d3748' : '#ffffff',
                    color: isDarkMode ? '#e2e8f0' : '#1a202c'
                  }} {...props} />
                ),
                th: ({node, ...props}) => (
                  <th style={{ 
                    border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                    padding: '12px 8px',
                    background: isDarkMode ? '#374151' : '#f7fafc',
                    color: isDarkMode ? '#f3f4f6' : '#1a202c',
                    fontWeight: '600',
                    textAlign: 'left'
                  }} {...props} />
                ),
                td: ({node, ...props}) => (
                  <td style={{ 
                    border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                    padding: '10px 8px',
                    background: isDarkMode ? '#2d3748' : '#ffffff',
                    color: isDarkMode ? '#e2e8f0' : '#1a202c'
                  }} {...props} />
                ),
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      customStyle={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '16px' }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props} style={{ 
                      background: isDarkMode ? '#1a202c' : '#f7fafc', 
                      padding: '3px 8px', 
                      borderRadius: '4px', 
                      fontSize: '16px', 
                      color: isDarkMode ? '#fbd38d' : '#d69e2e',
                      fontFamily: 'monospace'
                    }}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {asset.content}
            </ReactMarkdown>
          </div>
          
          <StudyQuestionsSection 
            questions={asset.studyQuestions}
            isDarkMode={isDarkMode}
          />
          
          <AssetQuizInterface 
            asset={asset} 
            isDarkMode={isDarkMode} 
            onQuizDataChange={setQuizData}
          />
          
          {/* Show message if quiz exists but not attempted */}
          {quizData.hasQuiz && !quizData.hasAttempts && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: isDarkMode ? '#2d3748' : '#fef5e7',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? '#4a5568' : '#f9e79f'}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <span style={{ fontSize: '20px' }}>ℹ️</span>
              <span style={{
                fontSize: '14px',
                color: isDarkMode ? '#fbbf24' : '#f39c12',
                fontWeight: '500'
              }}>
                You must complete the quiz at least once before marking this asset as complete.
              </span>
            </div>
          )}
          
          <label
            style={{
              marginTop: '24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '14px 18px',
              background: isDarkMode ? '#374151' : '#f7fafc',
              borderRadius: '8px',
              cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
              border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
              transition: 'all 0.2s',
              userSelect: 'none',
              opacity: (quizData.hasQuiz && !quizData.hasAttempts) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
                e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
              }
            }}
            onMouseLeave={(e) => {
              if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
                e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
              }
            }}
          >
            <input
              type="checkbox"
              checked={isCompleted}
              onChange={handleMarkComplete}
              disabled={quizData.hasQuiz && !quizData.hasAttempts}
              style={{
                width: '20px',
                height: '20px',
                cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
                accentColor: '#48bb78'
              }}
            />
            <span style={{
              fontSize: '15px',
              fontWeight: '600',
              color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
            }}>
              {isCompleted ? '✓ Completed' : 'Mark as Complete'}
            </span>
          </label>
        </div>
      </>
    );
  }

  // Markdown (from PDF)
  if (asset.assetType === 'markdown' && asset.content) {
    return (
      <>
        <div style={{
          background: isDarkMode ? '#2d3748' : '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
        }}>
        <div className="markdown-content" style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', fontSize: '18px', lineHeight: '1.8' }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, children, ...props}) => <h1 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h1>,
              h2: ({node, children, ...props}) => <h2 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginTop: '24px', marginBottom: '12px' }} {...props}>{children}</h2>,
              h3: ({node, children, ...props}) => <h3 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginTop: '20px', marginBottom: '10px' }} {...props}>{children}</h3>,
              h4: ({node, children, ...props}) => <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h4>,
              h5: ({node, children, ...props}) => <h5 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h5>,
              h6: ({node, children, ...props}) => <h6 style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</h6>,
              p: ({node, children, ...props}) => <p style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</p>,
              li: ({node, children, ...props}) => <li style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c', marginBottom: '6px' }} {...props}>{children}</li>,
              ul: ({node, children, ...props}) => <ul style={{ marginLeft: '20px', marginBottom: '16px' }} {...props}>{children}</ul>,
              ol: ({node, children, ...props}) => <ol style={{ marginLeft: '20px', marginBottom: '16px' }} {...props}>{children}</ol>,
              a: ({node, children, ...props}) => <a style={{ color: isDarkMode ? '#90cdf4' : '#667eea' }} {...props}>{children}</a>,
              strong: ({node, children, ...props}) => <strong style={{ color: isDarkMode ? '#f7fafc' : '#1a202c', fontWeight: '600' }} {...props}>{children}</strong>,
              em: ({node, children, ...props}) => <em style={{ color: isDarkMode ? '#e2e8f0' : '#1a202c' }} {...props}>{children}</em>,
              blockquote: ({node, ...props}) => (
                <blockquote style={{ 
                  borderLeft: `4px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  color: isDarkMode ? '#a0aec0' : '#718096',
                  fontStyle: 'italic',
                  background: isDarkMode ? '#1a202c' : '#f7fafc',
                  padding: '12px 16px',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }} {...props} />
              ),
              table: ({node, ...props}) => (
                <table style={{ 
                  borderCollapse: 'collapse',
                  width: '100%',
                  marginBottom: '16px',
                  background: isDarkMode ? '#2d3748' : '#ffffff',
                  color: isDarkMode ? '#e2e8f0' : '#1a202c'
                }} {...props} />
              ),
              th: ({node, ...props}) => (
                <th style={{ 
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  padding: '12px 8px',
                  background: isDarkMode ? '#374151' : '#f7fafc',
                  color: isDarkMode ? '#f3f4f6' : '#1a202c',
                  fontWeight: '600',
                  textAlign: 'left'
                }} {...props} />
              ),
              td: ({node, ...props}) => (
                <td style={{ 
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  padding: '10px 8px',
                  background: isDarkMode ? '#2d3748' : '#ffffff',
                  color: isDarkMode ? '#e2e8f0' : '#1a202c'
                }} {...props} />
              ),
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '16px' }}
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props} style={{ 
                    background: isDarkMode ? '#1a202c' : '#f7fafc', 
                    padding: '3px 8px', 
                    borderRadius: '4px', 
                    fontSize: '16px', 
                    color: isDarkMode ? '#fbd38d' : '#d69e2e',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </code>
                );
              }
            }}
          >
            {asset.content}
          </ReactMarkdown>
        </div>
        
        <StudyQuestionsSection 
          questions={asset.studyQuestions}
          isDarkMode={isDarkMode}
        />
        
        <AssetQuizInterface 
          asset={asset} 
          isDarkMode={isDarkMode} 
          onQuizDataChange={setQuizData}
        />
        
        {/* Show message if quiz exists but not attempted */}
        {quizData.hasQuiz && !quizData.hasAttempts && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: isDarkMode ? '#2d3748' : '#fef5e7',
            borderRadius: '8px',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#f9e79f'}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>ℹ️</span>
            <span style={{
              fontSize: '14px',
              color: isDarkMode ? '#fbbf24' : '#f39c12',
              fontWeight: '500'
            }}>
              You must complete the quiz at least once before marking this asset as complete.
            </span>
          </div>
        )}
        
        <label
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 18px',
            background: isDarkMode ? '#374151' : '#f7fafc',
            borderRadius: '8px',
            cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
            border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
            transition: 'all 0.2s',
            userSelect: 'none',
            opacity: (quizData.hasQuiz && !quizData.hasAttempts) ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
              e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
            }
          }}
          onMouseLeave={(e) => {
            if (!(quizData.hasQuiz && !quizData.hasAttempts)) {
              e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
            }
          }}
        >
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleMarkComplete}
            disabled={quizData.hasQuiz && !quizData.hasAttempts}
            style={{
              width: '20px',
              height: '20px',
              cursor: (quizData.hasQuiz && !quizData.hasAttempts) ? 'not-allowed' : 'pointer',
              accentColor: '#48bb78'
            }}
          />
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
          }}>
            {isCompleted ? '✓ Completed' : 'Mark as Complete'}
          </span>
        </label>
      </div>
      </>
    );
  }

  // External Link
  if (asset.assetType === 'link' && asset.externalUrl) {
    return (
      <div style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: isDarkMode ? '#a0aec0' : '#4a5568', marginBottom: '16px', fontSize: '16px' }}>
            This lesson contains an external resource:
          </p>
          <a
            href={asset.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '17px'
            }}
          >
            🔗 Open External Link
          </a>
        </div>
        
        <div style={{
          padding: '12px',
          background: isDarkMode ? '#1a202c' : '#f7fafc',
          borderRadius: '8px',
          fontSize: '15px',
          color: isDarkMode ? '#a0aec0' : '#718096',
          wordBreak: 'break-all'
        }}>
          {asset.externalUrl}
        </div>

        <label
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 18px',
            background: isDarkMode ? '#374151' : '#f7fafc',
            borderRadius: '8px',
            cursor: 'pointer',
            border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
            transition: 'all 0.2s',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
          }}
        >
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleMarkComplete}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#48bb78'
            }}
          />
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
          }}>
            {isCompleted ? '✓ Completed' : 'Mark as Complete'}
          </span>
        </label>
      </div>
    );
  }

  // Video
  if (asset.assetType === 'video') {
    const embedUrl = asset.externalUrl ? getVideoEmbedUrl(asset.externalUrl) : null;
    
    return (
      <div 
        onClick={() => onInteraction && onInteraction()}
        style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        {embedUrl ? (
          // Embedded YouTube or Vimeo video with tracking
          <div style={{ 
            position: 'relative', 
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div
              ref={videoContainerRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '8px'
              }}
            />
          </div>
        ) : asset.fileUrl ? (
          // Uploaded video file
          <video
            ref={videoRef}
            controls
            style={{
              width: '100%',
              maxHeight: '500px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}
            onEnded={handleMarkComplete}
          >
            <source src={asset.fileUrl} type={asset.fileType || 'video/mp4'} />
            Your browser does not support the video tag.
          </video>
        ) : (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', textAlign: 'center', padding: '40px' }}>
            No video available
          </p>
        )}

        <label
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 18px',
            background: isDarkMode ? '#374151' : '#f7fafc',
            borderRadius: '8px',
            cursor: 'pointer',
            border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
            transition: 'all 0.2s',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
          }}
        >
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleMarkComplete}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#48bb78'
            }}
          />
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
          }}>
            {isCompleted ? '✓ Completed' : 'Mark as Complete'}
          </span>
        </label>
      </div>
    );
  }

  // Document/PDF
  if (asset.assetType === 'document' && asset.fileUrl) {
    const isPDF = asset.fileType === 'application/pdf' || asset.fileUrl.toLowerCase().endsWith('.pdf');
    
    // Construct proper PDF URL
    const pdfUrl = asset.fileUrl.startsWith('http') ? asset.fileUrl : `${BASE_URL}${asset.fileUrl.startsWith('/') ? '' : '/'}${asset.fileUrl}`;
    
    return (
      <div style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        {isPDF ? (
          <>
            <div style={{ 
              position: 'relative', 
              width: '100%',
              height: '700px',
              borderRadius: '8px',
              marginBottom: '20px',
              overflow: 'hidden',
              border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
              background: '#525659'
            }}>
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                title={asset.title}
              />
              {/* Watermark overlay for PDFs */}
              {user && user.role === 'student' && (
                <VideoWatermark userName={user.name} userEmail={user.email} />
              )}
            </div>
            {/* Download option */}
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <a
                href={pdfUrl}
                download={asset.fileName || 'document.pdf'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  background: isDarkMode ? '#4a5568' : '#f7fafc',
                  color: isDarkMode ? '#e2e8f0' : '#4a5568',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '14px',
                  border: `1px solid ${isDarkMode ? '#718096' : '#e2e8f0'}`,
                  marginRight: '10px'
                }}
              >
                📥 Download PDF
              </a>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#ffffff',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                🔗 Open in New Tab
              </a>
            </div>
          </>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: isDarkMode ? '#a0aec0' : '#4a5568', marginBottom: '16px', fontSize: '16px' }}>
              Download the document to view:
            </p>
            <a
              href={asset.fileUrl}
              download
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '17px'
              }}
            >
              📄 Download Document
            </a>
          </div>
        )}

        <label
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 18px',
            background: isDarkMode ? '#374151' : '#f7fafc',
            borderRadius: '8px',
            cursor: 'pointer',
            border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
            transition: 'all 0.2s',
            userSelect: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
          }}
        >
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleMarkComplete}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#48bb78'
            }}
          />
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
          }}>
            {isCompleted ? '✓ Completed' : 'Mark as Complete'}
          </span>
        </label>
      </div>
    );
  }

  // Quiz Reference
  if (asset.assetType === 'quiz' && asset.contentReference?.refId) {
    // Show quiz interface if active
    if (isQuizActive && referencedContent) {
      return (
        <div style={{
          background: isDarkMode ? '#2d3748' : '#ffffff',
          borderRadius: '12px',
          padding: '24px',
          border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
        }}>
          <TakeQuiz 
            quiz={referencedContent} 
            onComplete={() => {
              setIsQuizActive(false);
              if (onComplete) {
                onComplete(asset._id);
              }
            }}
            isDarkMode={isDarkMode}
            embedded={true}
          />
        </div>
      );
    }

    // Show quiz preview/start screen
    return (
      <div 
        onClick={() => onInteraction && onInteraction()}
        style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        {loading ? (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>Loading quiz...</p>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#e53e3e', fontSize: '16px', marginBottom: '12px' }}>❌ {error}</p>
            <button
              onClick={fetchReferencedContent}
              className="btn btn-secondary"
              style={{ fontSize: '14px' }}
            >
              Try Again
            </button>
          </div>
        ) : referencedContent ? (
          <div>
            <div style={{
              padding: '20px',
              background: isDarkMode ? '#1a202c' : '#f7fafc',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '22px', color: isDarkMode ? '#e2e8f0' : '#1a202c', marginBottom: '8px' }}>
                📝 {referencedContent.title}
              </h3>
              {referencedContent.description && (
                <p style={{ color: isDarkMode ? '#a0aec0' : '#4a5568', fontSize: '16px', marginBottom: '12px' }}>
                  {referencedContent.description}
                </p>
              )}
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                fontSize: '14px', 
                color: isDarkMode ? '#a0aec0' : '#718096',
                marginTop: '12px'
              }}>
                <span>📋 {referencedContent.questions?.length || 0} questions</span>
                <span>⏱️ {referencedContent.duration || 0} minutes</span>
              </div>
            </div>
            
            <button
              className="btn btn-primary"
              onClick={() => setIsQuizActive(true)}
              style={{ fontSize: '17px' }}
            >
              Start Quiz →
            </button>
          </div>
        ) : (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
            Quiz not configured properly. Please contact your instructor.
          </p>
        )}
      </div>
    );
  }

  // Flashcard Reference
  if (asset.assetType === 'flashcard' && asset.contentReference?.refId) {
    const handleFlip = () => {
      setIsFlipped(!isFlipped);
      if (!isFlipped) {
        // Mark card as studied when flipping to see the answer
        setStudiedCards(prev => new Set([...prev, currentCardIndex]));
      }
    };

    const handleNext = () => {
      if (referencedContent?.terms && currentCardIndex < referencedContent.terms.length - 1) {
        setCurrentCardIndex(prev => prev + 1);
        setIsFlipped(false);
      }
    };

    const handlePrevious = () => {
      if (currentCardIndex > 0) {
        setCurrentCardIndex(prev => prev - 1);
        setIsFlipped(false);
      }
    };

    const handleCardClick = (index) => {
      setCurrentCardIndex(index);
      setIsFlipped(false);
    };

    const progressPercent = referencedContent?.terms?.length 
      ? Math.round((studiedCards.size / referencedContent.terms.length) * 100)
      : 0;

    return (
      <div 
        onClick={() => onInteraction && onInteraction()}
        style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        {loading ? (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>Loading flashcards...</p>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#e53e3e', fontSize: '16px', marginBottom: '12px' }}>❌ {error}</p>
            <button
              onClick={fetchReferencedContent}
              className="btn btn-secondary"
              style={{ fontSize: '14px' }}
            >
              Try Again
            </button>
          </div>
        ) : referencedContent && referencedContent.terms && referencedContent.terms.length > 0 ? (
          <div>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              background: isDarkMode ? '#1a202c' : '#f7fafc',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '20px', color: isDarkMode ? '#e2e8f0' : '#1a202c', marginBottom: '6px', fontWeight: '600' }}>
                🎴 {referencedContent.title}
              </h3>
              {referencedContent.description && (
                <p style={{ color: isDarkMode ? '#a0aec0' : '#4a5568', fontSize: '14px', marginBottom: '12px' }}>
                  {referencedContent.description}
                </p>
              )}
              
              {/* Progress Bar */}
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
                    Progress: {studiedCards.size} / {referencedContent.terms.length} cards studied
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#90cdf4' : '#667eea' }}>
                    {progressPercent}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: isDarkMode ? '#4a5568' : '#e2e8f0',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: isDarkMode ? '#4299e1' : '#667eea',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Flashcard */}
            <div style={{
              perspective: '1000px',
              marginBottom: '24px'
            }}>
              <div
                onClick={handleFlip}
                style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: '320px',
                  cursor: 'pointer',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.6s',
                  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
                }}
              >
                {/* Front of card - Term */}
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  minHeight: '320px',
                  backfaceVisibility: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '40px',
                  background: isDarkMode ? '#1a202c' : '#ffffff',
                  border: `2px solid ${isDarkMode ? '#4a5568' : '#cbd5e0'}`,
                  borderRadius: '12px',
                  boxShadow: isDarkMode 
                    ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                    : '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: isDarkMode ? '#90cdf4' : '#667eea',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Term
                  </div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '600',
                    color: isDarkMode ? '#e2e8f0' : '#1a202c',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {referencedContent.terms[currentCardIndex].term}
                  </div>
                  <div style={{
                    marginTop: 'auto',
                    paddingTop: '24px',
                    fontSize: '13px',
                    color: isDarkMode ? '#a0aec0' : '#718096'
                  }}>
                    Click to flip
                  </div>
                </div>

                {/* Back of card - Definition */}
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  minHeight: '320px',
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '40px',
                  background: isDarkMode ? '#2d3748' : '#f7fafc',
                  border: `2px solid ${isDarkMode ? '#4299e1' : '#667eea'}`,
                  borderRadius: '12px',
                  boxShadow: isDarkMode 
                    ? '0 4px 12px rgba(66, 153, 225, 0.3)' 
                    : '0 4px 12px rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: isDarkMode ? '#90cdf4' : '#667eea',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Definition
                  </div>
                  <div style={{
                    fontSize: '20px',
                    color: isDarkMode ? '#e2e8f0' : '#1a202c',
                    textAlign: 'center',
                    lineHeight: '1.6'
                  }}>
                    {referencedContent.terms[currentCardIndex].definition}
                  </div>
                  <div style={{
                    marginTop: 'auto',
                    paddingTop: '24px',
                    fontSize: '13px',
                    color: isDarkMode ? '#a0aec0' : '#718096'
                  }}>
                    Click to flip back
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <button
                onClick={handlePrevious}
                disabled={currentCardIndex === 0}
                style={{
                  padding: '10px 20px',
                  background: currentCardIndex === 0 
                    ? (isDarkMode ? '#2d3748' : '#f7fafc')
                    : (isDarkMode ? '#4a5568' : '#ffffff'),
                  color: currentCardIndex === 0 
                    ? (isDarkMode ? '#4a5568' : '#cbd5e0')
                    : (isDarkMode ? '#e2e8f0' : '#1a202c'),
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: currentCardIndex === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ← Previous
              </button>

              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: isDarkMode ? '#e2e8f0' : '#1a202c'
              }}>
                {currentCardIndex + 1} / {referencedContent.terms.length}
              </div>

              <button
                onClick={handleNext}
                disabled={currentCardIndex === referencedContent.terms.length - 1}
                style={{
                  padding: '10px 20px',
                  background: currentCardIndex === referencedContent.terms.length - 1
                    ? (isDarkMode ? '#2d3748' : '#f7fafc')
                    : (isDarkMode ? '#4299e1' : '#667eea'),
                  color: currentCardIndex === referencedContent.terms.length - 1
                    ? (isDarkMode ? '#4a5568' : '#cbd5e0')
                    : '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: currentCardIndex === referencedContent.terms.length - 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Next →
              </button>
            </div>

            {/* Card Grid Overview */}
            <div style={{
              padding: '16px',
              background: isDarkMode ? '#1a202c' : '#f7fafc',
              borderRadius: '8px'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: isDarkMode ? '#a0aec0' : '#718096',
                marginBottom: '12px'
              }}>
                Quick Navigation
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                gap: '8px'
              }}>
                {referencedContent.terms.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleCardClick(index)}
                    style={{
                      padding: '8px',
                      background: index === currentCardIndex
                        ? (isDarkMode ? '#4299e1' : '#667eea')
                        : studiedCards.has(index)
                        ? (isDarkMode ? '#2d3748' : '#e2e8f0')
                        : (isDarkMode ? '#1a202c' : '#ffffff'),
                      color: index === currentCardIndex
                        ? '#ffffff'
                        : studiedCards.has(index)
                        ? (isDarkMode ? '#90cdf4' : '#667eea')
                        : (isDarkMode ? '#718096' : '#a0aec0'),
                      border: `1px solid ${
                        index === currentCardIndex
                          ? (isDarkMode ? '#4299e1' : '#667eea')
                          : isDarkMode ? '#4a5568' : '#cbd5e0'
                      }`,
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Mark as Complete */}
            {progressPercent === 100 && (
              <label
                style={{
                  marginTop: '24px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '14px 18px',
                  background: isDarkMode ? '#374151' : '#f7fafc',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: `2px solid ${isCompleted ? '#48bb78' : '#cbd5e1'}`,
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? '#4b5563' : '#edf2f7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isDarkMode ? '#374151' : '#f7fafc';
                }}
              >
                <input
                  type="checkbox"
                  checked={isCompleted}
                  onChange={handleMarkComplete}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#48bb78'
                  }}
                />
                <span style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: isCompleted ? '#48bb78' : (isDarkMode ? '#e2e8f0' : '#2d3748')
                }}>
                  {isCompleted ? '✓ Completed' : 'Mark as Complete'}
                </span>
              </label>
            )}
          </div>
        ) : referencedContent ? (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
            No flashcard terms available yet.
          </p>
        ) : (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
            Flashcards not configured properly. Please contact your instructor.
          </p>
        )}
      </div>
    );
  }

  // Interview Reference
  if (asset.assetType === 'interview' && asset.contentReference?.refId) {
    return (
      <div 
        onClick={() => onInteraction && onInteraction()}
        style={{
        background: isDarkMode ? '#2d3748' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
      }}>
        {loading ? (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>Loading interview...</p>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: '#e53e3e', fontSize: '16px', marginBottom: '12px' }}>❌ {error}</p>
            <button
              onClick={fetchReferencedContent}
              className="btn btn-secondary"
              style={{ fontSize: '14px' }}
            >
              Try Again
            </button>
          </div>
        ) : referencedContent ? (
          <div>
            <div style={{
              padding: '20px',
              background: isDarkMode ? '#1a202c' : '#f7fafc',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '22px', color: isDarkMode ? '#e2e8f0' : '#1a202c', marginBottom: '8px' }}>
                🎤 {referencedContent.title}
              </h3>
              {referencedContent.description && (
                <p style={{ color: isDarkMode ? '#a0aec0' : '#4a5568', fontSize: '16px', marginBottom: '12px' }}>
                  {referencedContent.description}
                </p>
              )}
              <div style={{ fontSize: '15px', color: isDarkMode ? '#a0aec0' : '#718096' }}>
                <p>Questions: {referencedContent.questions?.length || 0}</p>
                <p>Duration: {referencedContent.duration || 'N/A'} minutes</p>
              </div>
            </div>
            
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/interview/${referencedContent._id}`)}
              style={{ fontSize: '17px' }}
            >
              Start Interview →
            </button>
          </div>
        ) : (
          <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
            Interview not configured properly. Please contact your instructor.
          </p>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <div 
      onClick={() => onInteraction && onInteraction()}
      style={{
      background: isDarkMode ? '#2d3748' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
      textAlign: 'center'
    }}>
      <p style={{ fontSize: '48px', marginBottom: '16px' }}>📦</p>
      <p style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
        Content not available or unsupported format
      </p>
    </div>
  );
}

export default AssetRenderer;
